import { InfoIcon } from "@chakra-ui/icons";
import { Box, Flex, Input, Text, Textarea, Tooltip } from "@chakra-ui/react";
import moment from "moment-timezone";
import { useMemo } from "react";
import "react-datepicker/dist/react-datepicker.css";
import ReactSelect from "react-select";
import countryList from "react-select-country-list";
import DynamicSelectField from "../../../Components/Common/DynamicSelectField";
import { useLazyDropdown } from "../../../hooks/useLazyDropdown";
import { usStateOptions } from "../../../utils/case/usStateOption";
import { sanitizeValueByType } from "../../../utils/sanitize";
import { selectStyles } from "../../../utils/selectStyel";
import MultiSelectComponents from "./MultiSelectComponents";
export const DataCard = ({
  label,
  value,
  isEditable,
  onChange,
  enumOptions,
  type,
  tooltip,
  code,
  referenceMap,
  multiSelectType,
}) => {
  const countryOptions = useMemo(() => countryList().getData(), []);
  let inferredType = "text";

  const { options, load, loading } = useLazyDropdown(
    code,
    referenceMap?.TIMESTAMP,
    referenceMap?.SUBGROUP
  );

  const typeUpper = String(type || "").toUpperCase();
  const labelUpper = String(label || "").toUpperCase();
  const isTypeDate = typeUpper === "DATE";
  const isTypeDateTime = typeUpper === "DATETIME";
  const isLabelClosedDate = labelUpper.includes("CLOSED DATE");

  if (isTypeDateTime) {
    inferredType = "datetime-local";
  } else if (isTypeDate) {
    inferredType = isLabelClosedDate ? "datetime-local" : "date";
  } else {
    inferredType = "text";
  }

  const formatDateValue = () => {
    if (!value) return "";
    const m = moment(value);
    if (!m.isValid()) return "";
    return inferredType === "datetime-local"
      ? m.format("YYYY-MM-DDTHH:mm")
      : m.format("YYYY-MM-DD");
  };

  const handleSanitizedChange = (val) => {
    const sanitized = sanitizeValueByType(val, inferredType, {
      options: enumOptions?.map((opt) => opt.CODE),
    });
    onChange?.(sanitized);
  };

  return (
    <Box borderWidth="1px" borderRadius="lg" p={2} boxShadow="md">
      <Box textAlign="center">
        <Flex alignItems="center" gap={2} mb={2}>
          <Text fontWeight={400} fontSize="md">
            {typeof label === "string" ? label : ""}
          </Text>
          {tooltip && (
            <Tooltip label={tooltip} fontSize="md" hasArrow placement="top">
              <InfoIcon />
            </Tooltip>
          )}
        </Flex>

        {type === "Dropdown" ? (
          <DynamicSelectField
            code={code}
            referenceMap={referenceMap}
            value={value ?? ""}
            isEditable={true}
            handleSanitizedChange={handleSanitizedChange}
            options={options}
            loading={loading}
            load={load}
          />
        ) : type === "Multiselect" ? (
          <MultiSelectComponents
            code={code}
            referenceMap={referenceMap}
            value={value ?? ""}
            isEditable={true}
            handleSanitizedChange={handleSanitizedChange}
            multiSelectType={multiSelectType}
            options={options}
            loading={loading}
            load={load}
          />
        ) : inferredType === "date" || inferredType === "datetime-local" ? (
          <Input
            type={inferredType}
            value={formatDateValue()}
            disabled={!isEditable}
            onChange={(e) => handleSanitizedChange(e.target.value)}
            borderRadius="sm"
          />
        ) : label === "State" ? (
          <Box>
            <ReactSelect
              options={usStateOptions}
              placeholder="Select State"
              isClearable
              isDisabled={!isEditable}
              value={usStateOptions.find((opt) => opt.value === value) || null}
              onChange={(selectedOption) =>
                handleSanitizedChange(selectedOption?.value || "")
              }
            />
          </Box>
        ) : label === "Country" || type === "Country" ? (
          <ReactSelect
            options={countryOptions}
            placeholder="Select Country"
            styles={selectStyles}
            menuPortalTarget={document.body}
            isClearable
            isDisabled={!isEditable}
            value={countryOptions.find((opt) => opt.value === value) || null}
            onChange={(selectedOption) =>
              handleSanitizedChange(selectedOption?.value || "")
            }
          />
        ) : type === "TextBox" ? (
          <Textarea
            value={value ?? ""}
            disabled={!isEditable}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={(e) => handleSanitizedChange(e.target.value)}
            height="20px"
          />
        ) : (
          <Input
            type={type === "Numeric" ? "number" : "text"}
            value={value ?? ""}
            disabled={!isEditable}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={(e) => handleSanitizedChange(e.target.value)}
            step={type === "Numeric" ? "any" : undefined}
            borderRadius="sm"
          />
        )}
      </Box>
    </Box>
  );
};
